import { ShieldCheck } from 'lucide-react';

import { Checkbox } from '@/shared/ui/checkbox';
import { titreCapacite, detailCapacite, LIBELLES_ROLES }
  from '@/features/admin/libellesCapacites';

/**
 * Le choix de privilèges, DANS la fiche de l'utilisateur qu'on est en train de regarder.
 *
 * POURQUOI CE BLOC EXISTE, ET POURQUOI L'ÉCRAN « Droits délégués » N'EXISTE PLUS.
 * Boubacar a recetté la page dédiée le 03/09 et a rejeté sa SURFACE, pas sa logique :
 * « ça n'est pas une page en plus, c'est un choix de privilèges qu'on doit pouvoir
 * faire directement dans la partie utilisateurs ». Sur la page dédiée, on choisissait
 * un rôle puis ses droits ; ici on est déjà sur la bonne personne, et le réglage se
 * fait sans quitter des yeux le compte concerné. Le mécanisme, lui, n'a pas bougé
 * d'une ligne : mêmes routes, même liste blanche, même plafond.
 *
 * ⚠️ CE N'EST PAS LA PROTECTION, et c'est la phrase la plus importante de ce fichier.
 * Décocher une case retire l'entrée du menu — parce que `/auth/me` publie les capacités
 * effectives — mais ce n'est PAS ce qui ferme l'accès. Le refus reste posé sur le
 * chemin de chaque requête par `require_cap` : l'adresse tapée à la main répond 403,
 * bouton ou pas. Le 30/08, croire l'inverse avait laissé 24 routes répondre 200
 * derrière un menu correctement masqué.
 *
 * ⚠️ LE RÉGLAGE PORTE SUR LE RÔLE, PAS SUR LA PERSONNE. Le serveur range les
 * délégations par rôle et par sous-compte (`tenants.delegations`), pas par utilisateur :
 * ouvrir un privilège ici l'ouvre à TOUS les comptes qui portent ce rôle. L'écran le
 * dit en toutes lettres au lieu de laisser croire à un réglage individuel — c'est
 * précisément le genre de chose qu'une interface ne doit pas faire deviner.
 *
 * LA LISTE N'EST PAS ÉCRITE ICI, elle est REÇUE. Ce qui est délégable, ce qui est
 * ouvert et ce que le rôle garde de toute façon viennent tous les trois de
 * `GET /admin/delegations`. Aucun code de capacité n'est écrit dans ce fichier : le
 * front ne peut donc pas proposer une case que le serveur refuserait.
 */
export default function PrivilegesDelegues({
  role, ligne, chargement, refus, ouvertes, onBasculer,
}) {
  const nomDuRole = LIBELLES_ROLES[role] || role;

  const cadre = (contenu) => (
    <div className="rounded-md border border-neutral-200 p-3" data-testid="privileges-delegues">
      <div className="flex items-center gap-1.5">
        <ShieldCheck className="w-3.5 h-3.5 text-neutral-400" />
        <span className="text-xs font-medium text-neutral-700">Privilèges délégués</span>
      </div>
      {contenu}
    </div>
  );

  if (chargement) {
    return cadre(<p className="mt-1.5 text-[11px] text-neutral-400">Chargement…</p>);
  }

  // Un écran ne masque pas un refus (`frontend/CLAUDE.md`) : sans ce cas, un 403 sur
  // la lecture des délégations afficherait « aucun privilège délégable », c'est-à-dire
  // une réponse métier là où il n'y a qu'une porte fermée.
  if (refus) {
    return cadre(
      <p className="mt-1.5 text-[11px] text-amber-700" data-testid="privileges-indisponibles">
        {refus}
      </p>);
  }

  const delegables = ligne?.delegables || [];
  if (delegables.length === 0) {
    return cadre(
      <p className="mt-1.5 text-[11px] text-neutral-400" data-testid="privileges-aucun">
        Aucun privilège n’est délégable au rôle {nomDuRole}.
      </p>);
  }

  return cadre(
    <div data-testid={`privileges-role-${role}`}>
      <p className="mt-1 text-[11px] text-neutral-400">
        Réglage du rôle <span className="text-neutral-600">{nomDuRole}</span> : il vaut
        pour tous les comptes qui le portent, pas seulement celui-ci.
        {' '}{ligne?.base?.length || 0} droit(s) acquis, non modifiables ici.
      </p>
      <div className="mt-2 space-y-1.5">
        {delegables.map((capacite) => (
          <label
            key={capacite}
            className="flex cursor-pointer items-start gap-2 rounded border border-neutral-200 p-2 hover:bg-neutral-50"
          >
            <Checkbox
              className="mt-0.5"
              checked={ouvertes.has(capacite)}
              onCheckedChange={() => onBasculer(capacite)}
              data-testid={`delegation-${role}-${capacite}`}
            />
            <span>
              <span className="block text-[12px] text-neutral-800">
                {titreCapacite(capacite)}
              </span>
              <span className="block text-[10px] text-neutral-500">
                {detailCapacite(capacite)}
              </span>
              <span className="mt-0.5 block font-mono text-[10px] text-neutral-400">
                {capacite}
              </span>
            </span>
          </label>
        ))}
      </div>
    </div>);
}
